import { useState, useContext, useEffect, useRef, useMemo } from "react";

//Components
import CheckboxWithLabel from "./CheckboxWithLabel";
import SingleDropDown from "./SingleDropDown";
import CircularProgress from "@material-ui/core/CircularProgress";
import { KeyboardDatePicker } from "@material-ui/pickers";
import {
  FormControl,
  Button,
  Box,
  TextareaAutosize,
  FormLabel,
} from "@material-ui/core";
import CustomAutoComplete from "./CustomAutoComplete";

//Contexts
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { UserContext } from "../Contexts/UserContext";
import { SetupContext } from "../Contexts/SetupContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";

//Styling
import { useStyles } from "../Styles/styles";

//Utils
import {
  findPrimaryOfficer,
  formatOfficerName,
  localGetData,
  parseObjectArrayIdString,
  pickPreferredCommonCallType,
} from "../utils/utils";
import { createEmptyIncidentReview } from "../utils/EmptyObjects";
import { MutationContext } from "../Contexts/MutationContext";
import { shouldBeLocked, checkPermission } from "../utils/Roles";
import { getValueFromEventValue } from "../utils/getValueFromEventValue";
import { SingleIncidentContext } from "../Contexts/SingleIncidentContext";
import { useApolloClient } from "@apollo/client";
import { useReviewValidation } from "../CustomHooks/useReviewValidation";
import ReviewFixModal from "./ReviewFixModal";
import moment from "moment";

const IncidentReview = ({
  incidentIndex,
  incident,
  incidentId,
  incidentUuid,
  incidentReviewLoading,
  incidentReviewError,
  incidentReview,
  disabled = undefined,
  userRoles,
  incidentOriId,
  setPausePolling,
  incidentPeople,
  _incidentReviewIsMounted,
  sizeAndWeightEnabledForIncident = false,
  axonEnabledForIncident = false,
  setPopper = () => {},
  ...props
}) => {
  const classes = useStyles();
  const {
    allOfficers,
    activeOfficersLoading,
    activeOfficersError,
    user,
    nightMode,
    onlineState,
  } = useContext(UserContext);
  const { yoeJuvenileStatuses } = useContext(SingleIncidentContext);
  const client = useApolloClient();
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const memoizedCanChangeIncidentDisposition = useMemo(
    () =>
      checkPermission(
        user?.roleNames,
        "change_incident_disposition",
        user?.editableOriIds,
        incidentOriId,
        incidentId,
      ),
    [user?.roleNames, user?.editableOriIds, incidentId, incidentOriId],
  );
  const memoizedCanApproveIncident = useMemo(
    () =>
      checkPermission(
        user?.roleNames,
        "approve_incident",
        user?.editableOriIds,
        incidentOriId,
        incidentId,
      ),
    [user?.roleNames, user?.editableOriIds, incidentId, incidentOriId],
  );

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const { setup, saOris } = useContext(SetupContext);
  const { UPDATE_INCIDENT_REVIEW, UPDATE_INCIDENT } = INCIDENT_ACTIONS;
  const { incidentDispatch, incidents } = useContext(IncidentsContext);
  const {
    updateIncidentReview,
    updateDrugTypes,
    updateIncidentFlags,
    updateCallType,
  } = useContext(MutationContext);

  const { reviewStatuses, approvalStatuses, drugTypes } = setup.setupLists;

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { incidentDispositionTypes, incidentFlags, classifications } =
    specificSetupLists || {};

  const [newReviewComment, setNewReviewComment] = useState("");

  useEffect(() => {
    _incidentReviewIsMounted.current = true;
    return () => {
      _incidentReviewIsMounted.current = false;
    };
  }, [_incidentReviewIsMounted]);

  useEffect(() => {
    if (
      !onlineState &&
      import.meta.env.VITE_FORCE_ONLINE !== "true" &&
      !incidentReview
    ) {
      const emptyIncidentReview = createEmptyIncidentReview(
        incidents[incidentIndex]?.incidentReviewId,
        saOris,
      );
      incidentDispatch({
        type: UPDATE_INCIDENT,
        payload: {
          id: incidentId,
          uuid: incidentUuid,
          property: "incidentReview",
          value: emptyIncidentReview,
        },
      });
      updateIncidentReview({
        variables: {
          input: {
            incidentReviewId: emptyIncidentReview.id,
            originatorId: user?.id,
            incidentId: incidentId,
            approvalStatusId: "1",
            reviewStatusId: "1",
            investigatingOfficerId:
              findPrimaryOfficer(
                incidents[incidentIndex]?.officerIncidentSpecifics,
              )?.id || null,
            saOriId: saOris?.[0]?.id || null,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
  }, [
    UPDATE_INCIDENT,
    incidentDispatch,
    incidentId,
    incidentIndex,
    incidentReview,
    incidentUuid,
    incidents,
    onlineState,
    saOris,
    updateIncidentReview,
    user?.id,
  ]);

  useEffect(() => {
    if (incidentReview && !incidentReview?.investigatingOfficer) {
      const primaryOfficer = findPrimaryOfficer(
        incidents[incidentIndex]?.officerIncidentSpecifics,
      );
      if (primaryOfficer) {
        incidentDispatch({
          type: UPDATE_INCIDENT_REVIEW,
          payload: {
            incidentIndex: incidentIndex,
            property: "investigatingOfficer",
            value: primaryOfficer,
          },
        });
        updateIncidentReview({
          variables: {
            input: {
              incidentReviewId: incidentReview.id,
              originatorId: user?.id,
              incidentId: incidentId,
              investigatingOfficerId: primaryOfficer?.id || null,
            },
          },
          context: {
            serializationKey: "incidentQueue",
          },
        });
      }
    }
  }, [
    UPDATE_INCIDENT_REVIEW,
    incidentDispatch,
    incidentId,
    incidentIndex,
    incidentReview,
    incidents,
    updateIncidentReview,
    user?.id,
  ]);

  const stateToMutation = {
    investigatingOfficer: "investigatingOfficerId",
    reviewStatus: "reviewStatusId",
    keepInOfficerQueue: "keepInOfficerQueue",
    statusDate: "statusDate",
    reviewingSupervisor: "reviewingSupervisorId",
    reviewDate: "reviewDate",
    approvalStatus: "approvalStatusId",
    incidentDispositionType: "incidentDispositionTypeId",
    submitToOri: "submitToOriId",
    submittedAt: "submittedAt",
    fulfilled: "fulfilled",
    keepInQueue: "keepInQueue",
    nibrsReviewed: "nibrsReviewed",
    reviewComments: "reviewComments",
    selectedDrugTypes: "drugTypeIds",
    incidentFlags: "incidentFlagIds",
    classification: "classificationId",
  };

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [missingSections, setMissingSections] = useState({});
  const [errors, setErrors] = useState([]);
  const [validationSource, setValidationSource] = useState(null);
  const [raceTicketId, setRaceTicketId] = useState(null);

  const raceTicket = useMemo(() => {
    if (!raceTicketId) return null;
    return incident?.incidentTickets?.find(ticket =>
      [ticket?.id, ticket?.uuid].some(
        value => value != null && String(value) === String(raceTicketId),
      ),
    );
  }, [incident?.incidentTickets, raceTicketId]);

  const { validateForReady } = useReviewValidation({
    incident,
    incidentPeople,
    yoeJuvenileStatuses,
    axonEnabledForIncident,
    poldOptOut: incident?.ori?.poldOptOut, // toggle as needed
    sizeAndWeightEnabledForIncident,
    userId: user?.id,
    client,
  });
  const runValidation = async (options, source) => {
    const { ok, errors, sections, warnings } = await validateForReady(options);

    // Non-blocking FYI alerts (YOE etc.)
    if (Array.isArray(warnings) && warnings.length > 0) {
      alert(warnings.join("\n"));
    }

    if (!ok) {
      setValidationSource(source || null);
      if (sections?.sizeAndWeight && Object.keys(sections).length === 1) {
        alert(errors.join("\n"));
        return false;
      }
      // Show ONE modal containing all missing sections
      // if (Array.isArray(errors) && errors.length > 0) {
      //   alert(errors.join("\n"));
      // }
      setMissingSections(sections || {});
      setErrors(errors || []);
      setRaceTicketId(sections?.race?.ticketId || null);
      setShowReviewModal(true);
      setPopper({
        open: true,
        content: `Validation errors:\n\n${errors.join("\n\n")}`,
        severity: "error",
      });
    } else {
      setMissingSections({});
      setErrors([]);
      setShowReviewModal(false);
      setValidationSource(null);
      setRaceTicketId(null);
      setPopper({
        open: true,
        content: "Validation Successful",
        severity: "success",
      });
    }
    return ok;
  };

  const handleChange = async event => {
    let name;
    let value;
    let mutationValue;
    if (event.target.type === "checkbox") {
      name = event.target.value;
      value = event.target.checked;
      mutationValue = value;
    } else {
      name = event.target.name;
      value = event.target.value;
      mutationValue = getValueFromEventValue(event.target.value);
    }
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`,
      );
      return null;
    }
    const fieldToUpdate = stateToMutation[name];

    if (name === "approvalStatus" && value?.name === "Approved") {
      const ok = await runValidation(undefined, "approvalStatus");
      if (!ok) return;
    }

    incidentDispatch({
      type: UPDATE_INCIDENT_REVIEW,
      payload: { incidentIndex: incidentIndex, property: name, value: value },
    });
    updateIncidentReview({
      variables: {
        input: {
          incidentReviewId: incidentReview.id,
          originatorId: user?.id,
          incidentId: incidentId,
          [fieldToUpdate]: mutationValue,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });

    switch (name) {
      case "reviewStatus":
        if (value.name === "Ready for Approval") {
          handleDateChange(null, "statusDate", moment());
        }
        return null;

      case "approvalStatus":
        if (value.name === "Approved") {
          handleDateChange(null, "reviewDate", moment());
          handleAutoCompleteChange(
            allOfficers?.find(o => o?.id?.toString() === user?.id?.toString()),
            "reviewingSupervisor",
          );
        }
        return null;

      default:
        return null;
    }
  };

  const handleDateChange = (value, name, date) => {
    const fieldToUpdate = stateToMutation[name];
    let serverTimeFormat = "";
    if (!date) {
      incidentDispatch({
        type: UPDATE_INCIDENT_REVIEW,
        payload: {
          incidentIndex: incidentIndex,
          property: name,
          value: serverTimeFormat,
        },
      });
      updateIncidentReview({
        variables: {
          input: {
            incidentReviewId: incidentReview.id,
            originatorId: user?.id,
            incidentId: incidentId,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    } else if (date.isValid()) {
      serverTimeFormat = date.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      incidentDispatch({
        type: UPDATE_INCIDENT_REVIEW,
        payload: {
          incidentIndex: incidentIndex,
          property: name,
          value: serverTimeFormat,
        },
      });
      updateIncidentReview({
        variables: {
          input: {
            incidentReviewId: incidentReview.id,
            originatorId: user?.id,
            incidentId: incidentId,
            [fieldToUpdate]: serverTimeFormat,
          },
        },
        context: {
          serializationKey: "incidentQueue",
        },
      });
    }
  };

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  const handleReviewFixUpdate = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    switch (name) {
      case "selectedDrugTypes": {
        let selectedDrugTypesIdString = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "drugTypes",
            value: value,
          },
        });
        updateDrugTypes({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: selectedDrugTypesIdString,
            },
          },
        });
        return null;
      }

      case "incidentFlags": {
        let incidentFlagIds = parseObjectArrayIdString(value);
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "incidentFlags",
            value: value,
          },
        });
        updateIncidentFlags({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: incidentFlagIds,
            },
          },
        });
        return null;
      }

      case "classification":
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: name,
            value: value,
          },
        });
        incidentDispatch({
          type: UPDATE_INCIDENT,
          payload: {
            id: incident.id,
            uuid: incident.uuid,
            property: "commonCallType",
            value: pickPreferredCommonCallType(
              value?.commonCallType,
              incident?.incidentDetail?.secondaryCallType?.commonCallType,
            ),
          },
        });
        updateCallType({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incident.uuid,
              originatorId: user?.id,
              [fieldToUpdate]: value?.id ? value.id : "",
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return null;

      default:
        alert(
          "Name not found in handleReviewFixUpdate function in IncidentReview.js",
        );
        return null;
    }
  };

  const handleAutoCompleteChange = (value, name) => {
    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_INCIDENT_REVIEW,
      payload: { incidentIndex: incidentIndex, property: name, value: value },
    });
    updateIncidentReview({
      variables: {
        input: {
          incidentReviewId: incidentReview.id,
          originatorId: user?.id,
          incidentId: incidentId,
          [fieldToUpdate]: value?.id ? value.id : value,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
  };

  const handleNewReviewComment = () => {
    let now = moment().format("MM/DD/YY HH:mm");
    let updatedReviewComment = `------------- ${user?.badge}: ${user?.lastName}, ${user?.firstName} - ${now} -------------\n${newReviewComment}\n\n`;
    if (incidentReview?.reviewComments) {
      updatedReviewComment =
        updatedReviewComment + incidentReview?.reviewComments;
    }
    incidentDispatch({
      type: UPDATE_INCIDENT_REVIEW,
      payload: {
        incidentIndex: incidentIndex,
        property: "reviewComments",
        value: updatedReviewComment,
      },
    });
    updateIncidentReview({
      variables: {
        input: {
          incidentReviewId: incidentReview.id,
          originatorId: user?.id,
          incidentId: incidentId,
          reviewComments: updatedReviewComment,
        },
      },
      context: {
        serializationKey: "incidentQueue",
      },
    });
    setNewReviewComment("");
  };

  const handleReviewStatusChange = async event => {
    const next = event?.target?.value;
    const valueName = next?.name;

    // Only validate when trying to set "Ready for Approval"
    if (valueName === "Ready for Approval") {
      const ok = await runValidation(
        { exclude: ["sizeAndWeight"] },
        "reviewStatus",
      );
      if (!ok) return;
    }

    // Either not "Ready for Approval", or all good—proceed as usual
    handleChange(event);
  };

  if (incidentReviewError)
    return (
      <div>
        {incidentReviewError.graphQLErrors.map((e, i) => {
          return <p key={i}>{e.message}</p>;
        })}
      </div>
    );
  if (incidentReview) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          maxWidth: "90vw",
          justifyContent: "flex-start",
          alignItems: "flex-end",
        }}
      >
        <CustomAutoComplete
          title={"Invest/Primary\xa0Officer"}
          loading={activeOfficersLoading}
          options={allOfficers}
          error={activeOfficersError}
          value={
            incidentReview?.investigatingOfficer
              ? incidentReview?.investigatingOfficer
              : {}
          }
          stateName={"investigatingOfficer"}
          updateFields={handleAutoCompleteChange}
          nameFormatter={formatOfficerName}
          userRoles={userRoles}
          {...props}
        />
        <SingleDropDown
          handleChange={handleReviewStatusChange}
          stateName={"reviewStatus"}
          value={incidentReview?.reviewStatus}
          options={reviewStatuses}
          title={"Ofc.\xa0Status"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          {...props}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={incidentReview?.keepInOfficerQueue || false}
          onChange={handleChange}
          stateName={"keepInOfficerQueue"}
          title={"Keep\xa0in\xa0Ofc\xa0Q"}
          userRoles={userRoles}
          {...props}
        />
        <FormControl variant="standard" className={classes.formControl}>
          <KeyboardDatePicker
            data-cy="status-date-picker"
            InputLabelProps={{ shrink: true }}
            clearable
            showTodayButton
            label={"Status\xa0Date"}
            value={
              incidentReview?.statusDate
                ? moment
                    .utc(incidentReview?.statusDate, "YYYY-MM-DD HH:mm:ss UTC")
                    .local()
                : null
            }
            onChange={(date, value) =>
              handleDateChange(value, "statusDate", date)
            }
            format="MM/DD/YYYY"
            disabled={memoizedLocked}
            ampm={false}
          />
        </FormControl>
        <CustomAutoComplete
          disabled={true}
          title={"Reviewing\xa0Supervisor"}
          loading={activeOfficersLoading}
          options={allOfficers}
          error={activeOfficersError}
          value={incidentReview?.reviewingSupervisor}
          stateName={"reviewingSupervisor"}
          updateFields={handleAutoCompleteChange}
          nameFormatter={formatOfficerName}
          userRoles={null}
          {...props}
        />
        <FormControl variant="standard" className={classes.formControl}>
          <KeyboardDatePicker
            data-cy="review-date-picker"
            InputLabelProps={{ shrink: true }}
            clearable
            showTodayButton
            label={"Review\xa0Date"}
            value={
              incidentReview?.reviewDate
                ? moment
                    .utc(incidentReview?.reviewDate, "YYYY-MM-DD HH:mm:ss UTC")
                    .local()
                : null
            }
            onChange={(date, value) =>
              handleDateChange(value, "reviewDate", date)
            }
            format="MM/DD/YYYY"
            ampm={false}
            disabled={memoizedLocked}
          />
        </FormControl>
        <SingleDropDown
          handleChange={handleChange}
          stateName={"approvalStatus"}
          value={incidentReview?.approvalStatus}
          options={approvalStatuses}
          title={"Approval"}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          disabled={!memoizedCanApproveIncident}
          {...props}
        />
        <SingleDropDown
          handleChange={handleChange}
          stateName={"incidentDispositionType"}
          value={incidentReview?.incidentDispositionType}
          options={incidentDispositionTypes}
          title={"Incident\xa0Disp."}
          classStyle={classes.formControl}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          userRoles={userRoles}
          disabled={!memoizedCanChangeIncidentDisposition}
          {...props}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={incidentReview?.fulfilled || false}
          onChange={handleChange}
          stateName={"fulfilled"}
          title={"Fulfilled"}
          userRoles={userRoles}
          {...props}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={incidentReview?.keepInQueue || false}
          onChange={handleChange}
          stateName={"keepInQueue"}
          title={"Keep\xa0in\xa0Sup\xa0Q"}
          userRoles={userRoles}
          {...props}
        />
        <CheckboxWithLabel
          formStyle={classes.formControl}
          checked={incidentReview?.nibrsReviewed || false}
          onChange={handleChange}
          stateName={"nibrsReviewed"}
          title={"NIBRS\xa0Reviewed"}
          userRoles={userRoles}
          {...props}
        />
        <FormControl fullWidth className={classes.formControl}>
          <FormLabel component="label">{"New\xa0Review\xa0Comment"}</FormLabel>
          <TextareaAutosize
            className={nightMode ? classes.textAreaDark : classes.textAreaLight}
            onChange={e => setNewReviewComment(e.target.value)}
            data-cy="newReviewComment"
            style={{
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              fontSize: "1rem",
              width: "100%",
            }}
            margin="dense"
            id="newReviewComment-id"
            value={newReviewComment || ""}
            minRows={2}
            name="newReviewComment"
            disabled={memoizedLocked}
          />
          <Box display="flex" flexDirection="row">
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
                marginRight: "2vw",
              }}
              disabled={memoizedLocked}
              variant="contained"
              onClick={handleNewReviewComment}
            >
              Submit
            </Button>
            <Button
              style={{
                width: "fit-content",
                marginTop: "1vh",
                marginBottom: "3vh",
              }}
              variant="contained"
              disabled={memoizedLocked}
              onClick={() => setNewReviewComment("")}
            >
              Cancel
            </Button>
          </Box>
          <FormLabel component="label">{"Review\xa0Comments"}</FormLabel>
          <TextareaAutosize
            className={nightMode ? classes.textAreaDark : classes.textAreaLight}
            // onChange={handleChange}
            disabled={memoizedLocked}
            data-cy="reviewComments"
            style={{
              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
              fontSize: "1rem",
              width: "100%",
            }}
            margin="dense"
            id="reviewComments-id"
            value={incidentReview?.reviewComments || ""}
            minRows={2}
            name="reviewComments"
          />
        </FormControl>
        <ReviewFixModal
          open={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          classes={classes}
          nightMode={nightMode}
          sections={missingSections}
          errors={errors}
          incident={incident}
          user={user}
          drugTypes={drugTypes}
          classifications={classifications}
          incidentFlags={incidentFlags}
          handleReviewFixUpdate={handleReviewFixUpdate}
          setPausePolling={setPausePolling}
          reviewStatuses={reviewStatuses}
          raceTicket={raceTicket}
          // If you want the RaceDataModal section to be able to re-trigger validation:
          handleReadyForApproval={handleReviewStatusChange}
          incidentPeople={incidentPeople}
          userRoles={userRoles}
          incidentIndex={incidentIndex}
          revalidateCallback={() => {
            const readyStatus = reviewStatuses?.find(
              status => status.name === "Ready for Approval",
            );
            const approvedStatus = approvalStatuses?.find(
              status => status.name === "Approved",
            );
            if (validationSource === "approvalStatus") {
              return handleChange({
                target: {
                  name: "approvalStatus",
                  value: approvedStatus,
                },
              });
            }
            return handleReviewStatusChange({
              target: {
                name: "reviewStatus",
                value: readyStatus,
              },
            });
          }}
          {...props}
        />
      </div>
    );
  } else {
    return (
      <div>
        <CircularProgress data-cy="loading-circle" />
      </div>
    );
  }
};

export default IncidentReview;
